export const SITE_NAME = 'Jinjiang Shoe Textile City'
export const DEFAULT_SITE_URL = 'http://onstc.com'
export const DEFAULT_DESCRIPTION =
  'Jinjiang Shoe Textile City connects global buyers with quality shoes, textiles, merchants, product sourcing and international trade services.'
export const DEFAULT_KEYWORDS =
  'Jinjiang Shoe Textile City, shoes, textile, global sourcing, footwear suppliers, textile suppliers, international trade'
export const DEFAULT_OG_IMAGE = '/og-image.svg'

const getBaseUrl = () => {
  const envUrl = process.env.VUE_APP_SITE_URL
  if (envUrl) return envUrl.replace(/\/$/, '')

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }

  return DEFAULT_SITE_URL
}

const getFullUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) return path
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${getBaseUrl()}${normalizedPath}`
}

const getAssetUrl = (path = DEFAULT_OG_IMAGE) => getFullUrl(path)

const upsertMetaByName = (name, content) => {
  if (typeof document === 'undefined' || !content) return

  let tag = document.head.querySelector(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', name)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

const upsertMetaByProperty = (property, content) => {
  if (typeof document === 'undefined' || !content) return

  let tag = document.head.querySelector(`meta[property="${property}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

const upsertLink = (rel, href) => {
  if (typeof document === 'undefined' || !href) return

  let tag = document.head.querySelector(`link[rel="${rel}"]`)
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    document.head.appendChild(tag)
  }
  tag.setAttribute('href', href)
}

const removeJsonLd = (id) => {
  if (typeof document === 'undefined') return
  const tag = document.getElementById(id)
  if (tag) tag.remove()
}

export const stripHtml = (value = '') =>
  String(value)
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()

export const truncateText = (value = '', maxLength = 160) => {
  const text = stripHtml(value)
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 1).trim()}...`
}

export const resolveImageUrl = (image, fallback = DEFAULT_OG_IMAGE) => {
  const value = String(image || '').trim()
  if (!value) return getAssetUrl(fallback)
  if (/^https?:\/\//i.test(value)) return value
  return getAssetUrl(value)
}

export const extractFirstImage = (html = '') => {
  const matched = String(html).match(/<img[^>]+src=["']([^"']+)["']/i)
  return matched?.[1] || ''
}

export const applySeo = ({
  title = SITE_NAME,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  robots = 'index,follow',
  structuredData = null,
} = {}) => {
  if (typeof document === 'undefined') return

  const pageTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`
  const canonicalUrl = getFullUrl(path)
  const imageUrl = resolveImageUrl(image)

  document.title = pageTitle
  upsertMetaByName('description', description)
  upsertMetaByName('keywords', keywords)
  upsertMetaByName('robots', robots)
  upsertMetaByName('twitter:card', 'summary_large_image')
  upsertMetaByProperty('og:type', type)
  upsertMetaByProperty('og:site_name', SITE_NAME)
  upsertMetaByProperty('og:title', pageTitle)
  upsertMetaByProperty('og:description', description)
  upsertMetaByProperty('og:url', canonicalUrl)
  upsertMetaByProperty('og:image', imageUrl)
  upsertLink('canonical', canonicalUrl)

  removeJsonLd('page-json-ld')
  if (structuredData) {
    const script = document.createElement('script')
    script.id = 'page-json-ld'
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)
  }
}

export const applyRouteSeo = (route) => {
  const seo = route.meta?.seo || {}
  applySeo({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    path: seo.path || route.path,
    image: seo.image,
    type: seo.type,
    robots: seo.robots,
    structuredData: seo.structuredData,
  })
}

export const getOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: getBaseUrl(),
  logo: getAssetUrl('/favicon.svg'),
  description: DEFAULT_DESCRIPTION,
})

export const getWebSiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: getBaseUrl(),
  description: DEFAULT_DESCRIPTION,
})
